import {Injectable} from "@angular/core";
import {AppService} from "@irb/app.service";
import {UtilityService} from "@irb-common/services/utility.service";
import {
    IPortfolioResultACCRebal,
    IPortfolioResultDialogData,
    IPortfolioResultLotRebalanceSummary,
    IPortfolioResultOrderTotal,
    IPortfolioResultPFSECRebal,
    IPortfolioResultRequest,
    IPortfolioResultResponse,
    IPortfolioResultSECRebal,
    SecurityTradeActionNote
} from "@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model";
import {IAlertDialogData} from "@irb/app.model";
import {IrbAppEvents, IrbClientsEvents, IrbRebalanceEvents} from "@irb-common/events";
import {PrValidationService} from "@irb/irb-editors/editor-portfolio-result/services/pr-validation.service";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class PrApplicationService {
  approveMSG: string;
  action: string;

  constructor(private _http: HttpClient,
              private as: AppService,
              private util: UtilityService,
              private prvs: PrValidationService
              ) {}

  checkWarningsAndApprove(data: IPortfolioResultResponse, isModified: boolean, cbFunc: Function) {
    if (this.checkUserTradeAccessOfAccounts(data, "APPROVE")) {
      if (data.hpnNo > 0) {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
          msg: "The portfolio " + data.portfolioName + " has " + data.hpnNo + " high priority notes.",
          dCancelBtnLabel: "Continue",
          dProceedBtnLabel: "View High Priority Notes",
          onCancel: () => {
            this.checkNegativeCashAndPerformAction(data, "negativeCashAndApprove", isModified, cbFunc);
          },
          onProceed: () => {
            this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_PORTFOLIO, {
              data: {"portfolioId": data.portfolioId},
              notesinit: true,
              slot: 'B'
            });
          }
        } as IAlertDialogData);
      } else {
        this.checkNegativeCashAndPerformAction(data, "negativeCashAndApprove", isModified, cbFunc);
      }
    }
  }

  checkUserTradeAccessOfAccounts(data: IPortfolioResultResponse, action: string) {
    if (!data.tradeAuth) {
      const dialogData = {
        title: "",
        message: "",
      } as IPortfolioResultDialogData;

      dialogData.title = "Status";

      if (action === "APPROVE") {
        dialogData.message = "Approve cannot be performed as either accounts are closed or the user is not authorized to the accounts of the portfolio result";
      } else if (action === "RECALL") {
        dialogData.message = "Recall cannot be performed as either accounts are closed or the user is not authorized to the accounts of the portfolio result";
      }

      this.prvs.displayPfSummryDialog(data, dialogData);
    }

    return data.tradeAuth;
  }

  checkNegativeCashAndPerformAction(data: IPortfolioResultResponse, action: string, isModified: boolean, cbFunc: Function) {
    let warning = false;

    data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
      if(!warning) {
        const isNgtvCashWarngForAccShown = this.util.getObjectValue(item, action) || false;
        const availCashAfterAdj = item.afterRebalAvailAmt;

        if (!isNgtvCashWarngForAccShown && availCashAfterAdj < 0) {
          warning = true;
          this.util.setObjectValue(item, action, true); //show cash warning once for an acc on approve
          const dialogData: any = {};
          dialogData.dialogFor = action;
          dialogData.title = "Warning";
          dialogData.message = "The cash in account " + item.accountNum + " has gone negative";
          dialogData.cashItem = item;
          dialogData.onComplete = (() => {
            this.checkNegativeCashAndPerformAction(data, dialogData.dialogFor, isModified, cbFunc);
            this.prvs.completeValidation();
          });
          this.prvs.displayPfSummryConfDialog(data, null, null, dialogData);
        }
      }
    });

    if (!warning) {
      if (action === "negativeCashAndApprove") {
        cbFunc(this.getPortfolioResultRequest(data, isModified, "VALIDATE_AND_APPROVE"));
      } else if (action === "negativeCashAndSave") {
        cbFunc(this.getPortfolioResultRequest(data, isModified, "SAVE"));
      }
    }
  }

  checkPfDeleted(data: IPortfolioResultResponse, action: string) {
    if (data.portfolioDeleted) {
      const dialogData: IAlertDialogData = {} as IAlertDialogData;

      if (action === "REACTIVATE") {
        dialogData.dTitle = "Cannot Enable Result";
        dialogData.msg = "This result cannot be enabled because the portfolio " + data.portfolioName + " has been deleted.\nTo reinstate the portfolio so that this result can be enabled, create the portfolio using the same name and the same assigned accounts listed in this result";
      } else if (action === "RECALL") {
        dialogData.dTitle = "Cannot Recall Result";
        dialogData.msg = "This result cannot be recalled  because the portfolio " + data.portfolioName + " has been deleted.\nTo reinstate the portfolio so that this result can be recalled, create the portfolio using the same name and the same assigned accounts listed in this result";
      }

      this.as.fireEvent(IrbAppEvents.SHOW_ALERT, dialogData);
    } else {
      if (action === "REACTIVATE") {
        this.reActivateResult(data);
      } else if (action === "RECALL") {
        if (this.checkUserTradeAccessOfAccounts(data, "RECALL")) {
          const r = this.getPortfolioResultRequest(data, false, "RECALL");
          this.as.fireEvent(IrbRebalanceEvents.SAVE_PORTFOLIO_RESULT_DETAIL, r);
        }
      }
    }
  }

  reActivateResult(data: IPortfolioResultResponse) {
    const dialogData = {} as IPortfolioResultDialogData;
    dialogData.title = "Re-enable this result";
    dialogData.dialogFor = "reActivateResult";
    dialogData.message = "When you re-enable this result, it will become active. Any other active result for this portfolio will become inactive, because you can only have one active result for a portfolio.";
    dialogData.onComplete = (() => {
        data.refusedGainsThreshold = false;
      const r = this.getPortfolioResultRequest(data, false, "REACTIVATE");
      this.as.fireEvent(IrbRebalanceEvents.SAVE_PORTFOLIO_RESULT_DETAIL, r);
    });
    this.prvs.displayPfSummryConfDialog(data, null, null, dialogData);
  }

  // NOT IN USE
  // tradeModified(isModified: boolean) {
  //   console.log(isModified);
  // }

  getPortfolioResultRequest(data: IPortfolioResultResponse, isModified: boolean, action: string): IPortfolioResultRequest {
    let r: IPortfolioResultRequest = {} as IPortfolioResultRequest;
    this.action = action;
    const approveMSGValue: string = "Approval can't be completed";

    if (isModified) {
      r = this.getpfSumryScreenData(data);

      switch (action) {
        case "VALIDATE_AND_APPROVE":
          r.method = "saveAndValidateAndApproveTrades";
          this.approveMSG = approveMSGValue;
          break;
        case "APPROVE":
          r.method = "saveAndApproveTrades";
          this.approveMSG = approveMSGValue;
          break;
        case "REFUSE":
          r.method = "saveAndRefuseTrades";
          break;
        case "REFUSE_AND_RESUBMIT":
          r.method = "saveAndRefuseAndResubmit";
          r.newTradeExplanation = data.newTradeExplanation;
          r.newRebalanceName = data.newRebalanceName;
          break;
        case "SAVE":
          r.method = "saveDetails";
          break;
      }
    } else {
      r.rebalId = data.rebalId;
      r.portfolioId = data.portfolioId;
      r.rebalStatus = data.rebalStatus;
      r.versionId = data.versionId;

      switch (action) {
        case "VALIDATE_AND_APPROVE":
          r.method = "validateAndApproveTrades";
          r = this.getDataForApprove(data, r);
          this.approveMSG = approveMSGValue;
          break;
        case "APPROVE":
          r.method = "approveTrades";
          r = this.getDataForApprove(data, r);
          this.approveMSG = approveMSGValue;
          break;
        case "REFUSE":
          r.method = "refuseTrades";
          break;
        case "RECALL":
          r.method = "recallTrades";
          r.totalTradeCount = this.getTotalOrdersCount(data);
          break;
        case "REFUSE_AND_RESUBMIT":
          r.method = "refuseAndResubmit";
          r.newTradeExplanation = data.newTradeExplanation;
          r.newRebalanceName = data.newRebalanceName;
          break;
        case "REACTIVATE":
          r.method = "reenableResult";
          break;
      }
    }

    return r;
  }
  
  getpfSumryScreenData(data: IPortfolioResultResponse): IPortfolioResultRequest {
    const _data = this.util.clone(data);
    const isCashEqvEnabled = this.as.getItem("IRB_APP_CONFIG")?.featureFlags?.isCashEqvEnabled || false;
    _data.secRebalDTOList.forEach((item: IPortfolioResultSECRebal) => {
      if (item.txnType !== "NONE" && item.txnAmt === 0) {
        item.txnType = "NONE";

        item.lotRebalanceSummaryDTOList.forEach((lot: IPortfolioResultLotRebalanceSummary) => {
          lot.txnType = "NONE";
        });
      }
    });

    const d = {} as IPortfolioResultRequest;
    d.rebalId = _data.rebalId;
    d.portfolioId = _data.portfolioId;
    d.rebalStatus = _data.rebalStatus;
    d.userRebalStatus = _data.userRebalStatus;
    d.tradeExplanation = _data.tradeExplanation;
    d.portfolioAccNumList = _data.portfolioAccNumList;
    d.versionId = _data.versionId;
    d.mktValAfterAdj = _data.mktValAfterAdj;
    d.revertUserEdits = _data.revertUserEdits;
    d.iRebalAppvStatus = _data.iRebalAppvStatus;
    d.envGlobalSettingsDTO = _data.envGlobalSettingsDTO;
    d.userEdit = _data.userEdit;
    d.orderTotalDTOList = this.util.clone(_data.orderTotalDTOList);
    d.accRebalDTOList = [];
    d.secRebalDTOList = [];
    d.pfSecRebalDTOList = [];

    _data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
      if (isCashEqvEnabled) {
        d.accRebalDTOList.push(this.util.getObjectValues(item, ["accId", "accountNum", "cashAdj", "cashMin", "cashTarget", "afterRebalTotalCash", "afterRebalAvailAmt", "cashEquivAfterAdj", "cashEquivAdj"]));
      } else {
        d.accRebalDTOList.push(this.util.getObjectValues(item, ["accId", "accountNum", "cashAdj", "cashMin", "cashTarget", "afterRebalTotalCash", "afterRebalAvailAmt"]));
      }
    });

    _data.secRebalDTOList.forEach((item: IPortfolioResultSECRebal) => {
      const a = [] as Array<IPortfolioResultSECRebal>;

      item.lotRebalanceSummaryDTOList.forEach((lot: IPortfolioResultSECRebal) => {
        a.push(this.util.getObjectValues(lot, ["txnType","txnAmt","txnShares","currentShares","txnSharesLocked","txnAmtEdit","shareQtyEdit", "afterRebalMktVal", "afterRebalShares","realzdGainLoss","penaltyAmt","sellTerm","rebalStatus","quantity","mktValue","lotHoldingsId","rebalLotId"]));
      });

      item.lotRebalanceSummaryDTOList = a;

      d.secRebalDTOList.push(this.util.getObjectValues(item, ["rebalSecId", "securityId", "ordHarvestedSecId", "secDeleted", "secAccId", "secSymbol", "secType", "accountNum", "accountType", "txnType", "txnAmt", "txnShares", "txnCurrency", "txnEditIn", "currentShares", "txnSharesLocked", "txnAmtEdit", "shareQtyEdit", "afterRebalMktVal", "afterRebalShares", "realzdGainLoss", "penaltyAmt", "sellTerm", "washSell", "washSaleLotSoldAtLoss", "rebalStatus", "quantity", "mktValue", "lotRebalanceSummaryDTOList", "lotHoldingsId", "rebalLotId", "capGainsReinvest", "dividendReinvest", "accountLocationType", "swapExchange", "swapExchangeMapId", "swapExchangeTxnType", "currMarketPrice", "ccDNSRestriction", "isRollTransaction", "rollId", "txnTypeEdit"]));
    });

    _data.pfSecRebalDTOList.forEach((item: IPortfolioResultPFSECRebal) => {
        if (!item.isEquivalentChild)
            d.pfSecRebalDTOList.push(this.util.getObjectValues(item, ["secId", "secAdjAmt", "secMktValAfterAdj", "secPercntValAfterAdj", "secRelVarianceAfterAdj", "secAbsVarianceAfterAdj", "secMktVal", "secModelTargetPercnt", "secCurPercntVal", "secMinTolPercnt", "secMaxTolPercnt", "secExclusionType"]));
    });
    
    if(d.revertUserEdits) {
      d.orderTotalDTOList.forEach((item: IPortfolioResultOrderTotal) => {
        item.userTotValue = 0;
      });
    }

    d.tradeExplanationEdited = _data.tradeExplanationEdited;
    d.allOrdersCleared = _data.allOrdersCleared;

    return d;
  }

  getDataForApprove(data: IPortfolioResultResponse, actionData: any) {
    actionData.portfolioAccNumList = data.portfolioAccNumList;
    actionData.secRebalDTOList = [];
    actionData.accRebalDTOList = [];

    data.secRebalDTOList.forEach((item: IPortfolioResultSECRebal) => {
      actionData.secRebalDTOList.push(this.util.getObjectValues(item, ["rebalSecId", "mktValue", "securityId", "secDeleted", "secAccId", "secSymbol", "accountNum", "accountName", "accountType", "txnType", "txnAmt", "secType", "ordHarvestedSecId", "txnShares", "txnSharesLocked", "quantity", "capGainsReinvest", "dividendReinvest", "swapExchange", "swapExchangeMapId", "swapExchangeTxnType", "isRollTransaction", "rollId"]));
    });

    data.accRebalDTOList.forEach((item: IPortfolioResultACCRebal) => {
      actionData.accRebalDTOList.push(this.util.getObjectValues(item, ["accountNum"]));
    });

    return actionData;
  }

  getTotalOrdersCount(data: IPortfolioResultResponse): number {
    let totIRblOrder = 0;
    let totUsrOrder = 0;

    data.orderTotalDTOList.forEach((item: IPortfolioResultOrderTotal) => {
      if (item.rowHdrStr === "Total (absolute)") {
        totIRblOrder = item.quantity;
        totUsrOrder = item.userQuantity;
      }
    });

    if (totUsrOrder > 0) {
      return totUsrOrder;
    } else {
      return totIRblOrder;
    }
  }

  getSecurityTradeActionNote(data: IPortfolioResultResponse, securitySymbol: string): Array<SecurityTradeActionNote> {
    return data.rebalSecReportList.filter((n: SecurityTradeActionNote) => n.securitySymbol === securitySymbol);
  }
}
